<script lang="ts" setup>
import { ref, computed, onMounted, watch, reactive } from 'vue'
import { parsePhone, parsePastedPhone } from '@/utils/phone'
import { supportedLanguages, type SupportedLanguageCode } from 'config/app'
import { all as getAllCountries, type CountryData } from 'country-codes-list'
import { useValidationRules } from '@/composables/useValidationRules'
import BaseSelect from '@/components/base/BaseSelect.vue'

export interface ContactInfoData {
  phone: string
  email: string
  lang: SupportedLanguageCode | string
  ust_id: string
}

// Validation
const { phoneDetailed } = useValidationRules()

const errors = reactive({
  phone: '' as string | true
})

const touched = reactive({
  phone: false
})

const validatePhone = (value: string): string | true => {
  return phoneDetailed(value)
}

const handlePhoneBlur = () => {
  touched.phone = true
  errors.phone = validatePhone(tempPhone.value)
}

const getPhoneError = (): string => {
  const error = errors.phone
  return typeof error === 'string' && error !== '' ? error : ''
}

const props = defineProps<{
  modelValue: ContactInfoData
  disabled: boolean
  isPerson: boolean
  /** When address country changes, sync phone country code */
  addressCountryCode?: string
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: ContactInfoData): void
  (e: 'openEmailChange'): void
}>()

// Country codes data for phone country selection
const countryCodes = ref<CountryData[]>([])
const phoneCountryData = ref<CountryData | null>(null)
const tempPhone = ref('')

// Get list of calling codes for paste detection
const knownCallingCodes = computed(() => countryCodes.value.map((c) => c.countryCallingCode))

// Initialize phone from props
const parsePhoneFromProps = () => {
  const phone = props.modelValue.phone
  if (!phone) return

  const { countryCode, local } = parsePhone(phone)
  const callingCode = countryCode.replace('+', '')

  // Remove leading zero from local number (German style)
  tempPhone.value = local.startsWith('0') ? local.slice(1) : local

  const foundCountry = countryCodes.value.find((c) => c.countryCallingCode === callingCode)
  if (foundCountry) {
    phoneCountryData.value = foundCountry
  }
}

// Initialize on mount
onMounted(() => {
  countryCodes.value = getAllCountries().sort((a, b) =>
    a.countryNameEn.localeCompare(b.countryNameEn)
  )

  // Set default to Germany
  phoneCountryData.value = countryCodes.value.find((c) => c.countryCode === 'DE') || null

  // Parse phone from props
  parsePhoneFromProps()
})

// Watch for prop changes
watch(
  () => props.modelValue.phone,
  () => {
    if (!tempPhone.value) {
      parsePhoneFromProps()
    }
  }
)

// Sync phone country with address country when it changes
watch(
  () => props.addressCountryCode,
  (newCountry) => {
    if (newCountry) {
      const matchingCountry = countryCodes.value.find(
        (c) => c.countryCode.toUpperCase() === newCountry.toUpperCase()
      )
      if (matchingCountry) {
        phoneCountryData.value = matchingCountry
        // Only emit if there's actual phone content to avoid corrupting with empty local part
        if (tempPhone.value) {
          updatePhoneInModelValue()
        }
      }
    }
  }
)

// Handle paste event for phone field
const handlePhonePaste = (event: ClipboardEvent) => {
  const pasted = event.clipboardData?.getData('text') || ''

  // Only intercept if it looks like a full phone number
  if (
    pasted.startsWith('+') ||
    pasted.startsWith('00') ||
    /^\d{10,}$/.test(pasted.replace(/\D/g, ''))
  ) {
    event.preventDefault()

    const currentCode = '+' + (phoneCountryData.value?.countryCallingCode || '49')
    const { countryCode, local } = parsePastedPhone(pasted, currentCode, knownCallingCodes.value)

    // Find matching country data
    const matchingCountry = countryCodes.value.find((c) => '+' + c.countryCallingCode === countryCode)

    if (matchingCountry) {
      phoneCountryData.value = matchingCountry
    }
    tempPhone.value = local
    updatePhoneInModelValue()
  }
}

// Handle typing detection for country code (detect +XX or 00XX at start)
const handlePhoneInput = () => {
  const input = tempPhone.value

  // Check if input starts with + or 00 (international format)
  if (input.startsWith('+') || input.startsWith('00')) {
    const currentCode = '+' + (phoneCountryData.value?.countryCallingCode || '49')
    const { countryCode, local } = parsePastedPhone(input, currentCode, knownCallingCodes.value)

    // Find matching country data
    const matchingCountry = countryCodes.value.find((c) => '+' + c.countryCallingCode === countryCode)

    if (matchingCountry) {
      phoneCountryData.value = matchingCountry
    }
    tempPhone.value = local
    updatePhoneInModelValue()
  }
}

// Sanitize local phone number by removing any accidentally included country code or prefix
const sanitizeLocalPhone = (local: string, countryCode: string): string => {
  let cleaned = local.trim()

  // Remove any + prefix and country code if accidentally included
  if (cleaned.startsWith('+')) {
    cleaned = cleaned.slice(1)
    if (cleaned.startsWith(countryCode)) {
      cleaned = cleaned.slice(countryCode.length)
    }
  }
  // Remove 00 prefix and country code
  if (cleaned.startsWith('00')) {
    cleaned = cleaned.slice(2)
    if (cleaned.startsWith(countryCode)) {
      cleaned = cleaned.slice(countryCode.length)
    }
  }
  // Remove leading zero (area code style)
  if (cleaned.startsWith('0')) {
    cleaned = cleaned.slice(1)
  }
  // Remove any remaining non-digit characters
  return cleaned.replace(/\D/g, '')
}

// Update phone in model value
const updatePhoneInModelValue = () => {
  const countryCode = phoneCountryData.value?.countryCallingCode || '49'
  const localPhone = sanitizeLocalPhone(tempPhone.value, countryCode)
  // Only include phone if local part is not empty
  const fullPhone = localPhone ? '+' + countryCode + '.' + localPhone : ''

  emit('update:modelValue', {
    ...props.modelValue,
    phone: fullPhone
  })
}

// Watch for local phone changes
watch(tempPhone, () => {
  updatePhoneInModelValue()
  // Real-time validation if field was touched
  if (touched.phone) {
    errors.phone = validatePhone(tempPhone.value)
  }
})

watch(phoneCountryData, () => {
  // Only emit if there's actual phone content to avoid corrupting with empty local part
  if (tempPhone.value) {
    updatePhoneInModelValue()
  }
})

const updateField = <K extends keyof ContactInfoData>(field: K, value: ContactInfoData[K]) => {
  emit('update:modelValue', {
    ...props.modelValue,
    [field]: value
  })
}
</script>

<template>
  <div class="form-section">
    <h3 class="form-section-title">
      <v-icon icon="mdi-phone" size="18" />
      <span>{{ $t('dashboard.account.stammdaten.contact') }}</span>
    </h3>

    <div class="form-row">
      <div class="form-group phone-group">
        <label class="form-label">{{ $t('dashboard.account.stammdaten.phone') }}</label>
        <div class="phone-input-wrapper">
          <v-autocomplete
            v-model="phoneCountryData"
            :items="countryCodes"
            :disabled="disabled"
            density="compact"
            item-title="countryNameEn"
            item-value="countryCallingCode"
            return-object
            variant="outlined"
            hide-details
            class="phone-code-select"
            :class="{ disabled: disabled }"
            :menu-props="{ maxHeight: 300, width: 'auto', minWidth: '280px' }"
          >
            <template #selection="{ item }">
              <span class="phone-code-selection">
                <span class="flag">{{ item.flag }}</span>
                <span>+{{ item.countryCallingCode }}</span>
              </span>
            </template>
            <template #item="{ props: itemProps, item }">
              <v-list-item v-bind="itemProps" class="country-item">
                <template #prepend>
                  <span class="flag-large">{{ item.flag }}</span>
                </template>
                <template #title>{{ item.countryNameEn }}</template>
                <template #subtitle>+{{ item.countryCallingCode }}</template>
              </v-list-item>
            </template>
          </v-autocomplete>
          <input
            v-model="tempPhone"
            type="tel"
            class="form-input phone-number-input"
            :disabled="disabled"
            :class="{ disabled: disabled, 'has-error': getPhoneError() }"
            :placeholder="$t('dashboard.account.stammdaten.phoneNumber')"
            @input="handlePhoneInput"
            @paste="handlePhonePaste"
            @blur="handlePhoneBlur"
          />
        </div>
        <Transition name="error-slide">
          <span v-if="getPhoneError()" class="field-error">{{ getPhoneError() }}</span>
        </Transition>
      </div>
      <div class="form-group">
        <label class="form-label">{{ $t('dashboard.account.stammdaten.email') }}</label>
        <div class="email-input-wrapper">
          <input
            :value="modelValue.email"
            type="email"
            class="form-input"
            disabled
            :placeholder="$t('dashboard.account.stammdaten.emailAddress')"
          />
          <button type="button" class="btn btn-email-change" @click="emit('openEmailChange')">
            <v-icon icon="mdi-pencil" size="16" />
            <span>{{ $t('dashboard.account.stammdaten.emailChange.title') }}</span>
          </button>
        </div>
      </div>
    </div>

    <div class="form-row">
      <div class="form-group">
        <label class="form-label">{{ $t('dashboard.account.stammdaten.language') }}</label>
        <BaseSelect
          :model-value="modelValue.lang"
          :items="supportedLanguages"
          :disabled="disabled"
          item-title="label"
          item-value="code"
          @update:model-value="updateField('lang', $event as SupportedLanguageCode)"
        >
          <template #selection="{ item }">
            <span class="language-selection">
              <span class="flag">{{ (item as any).flag }}</span>
              <span>{{ (item as any).label }}</span>
            </span>
          </template>
          <template #option="{ item }">
            <span class="flag-large">{{ (item as any).flag }}</span>
            <span>{{ (item as any).label }}</span>
          </template>
        </BaseSelect>
      </div>
    </div>

    <div v-if="!isPerson" class="form-row">
      <div class="form-group">
        <label class="form-label">{{ $t('dashboard.account.stammdaten.vatId') }}</label>
        <input
          :value="modelValue.ust_id"
          type="text"
          class="form-input"
          :disabled="disabled"
          :class="{ disabled: disabled }"
          placeholder="DE123456789"
          @input="updateField('ust_id', ($event.target as HTMLInputElement).value)"
        />
        <span class="form-hint">{{ $t('dashboard.account.stammdaten.vatIdHint') }}</span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.form-section {
  margin-bottom: 32px;
  padding-bottom: 28px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.08);

  &:last-of-type {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
}

.form-section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(200, 218, 255, 0.7);
  margin: 0 0 20px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;

  &.phone-group {
    flex: 1;
    min-width: 0;
  }
}

.form-label {
  font-size: 0.8rem;
  font-weight: 500;
  color: rgba(200, 218, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.form-input {
  width: 100%;
  padding: 12px 14px;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 10px;
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.95);
  transition: all 0.2s ease;

  &::placeholder {
    color: rgba(200, 218, 255, 0.3);
  }

  &:focus {
    outline: none;
    border-color: #00b0ff;
    background: rgba(0, 176, 255, 0.05);
    box-shadow: 0 0 0 3px rgba(0, 176, 255, 0.1);
  }

  &:disabled,
  &.disabled {
    background: rgba(255, 255, 255, 0.02);
    border-color: rgba(120, 163, 255, 0.08);
    color: rgba(255, 255, 255, 0.6);
    cursor: not-allowed;
  }

  &.has-error {
    border-color: #ef5350;
    background: rgba(244, 67, 54, 0.05);

    &:focus {
      border-color: #ef5350;
      box-shadow: 0 0 0 3px rgba(244, 67, 54, 0.1);
    }
  }
}

/* Field Error */
.field-error {
  display: block;
  font-size: 0.75rem;
  color: #ef5350;
  margin-top: 4px;
}

.form-hint {
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.4);
}

/* Error Transition */
.error-slide-enter-active {
  transition: all 0.2s ease;
}

.error-slide-leave-active {
  transition: all 0.15s ease;
}

.error-slide-enter-from,
.error-slide-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* Email Input with Change Button */
.email-input-wrapper {
  display: flex;
  gap: 10px;
  align-items: stretch;

  .form-input {
    flex: 1;
  }
}

.btn-email-change {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  background: rgba(0, 176, 255, 0.1);
  color: #00b0ff;
  border: 1px solid rgba(0, 176, 255, 0.25);
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: rgba(0, 176, 255, 0.15);
    border-color: rgba(0, 176, 255, 0.35);
  }
}

/* Phone Input */
.phone-input-wrapper {
  display: flex;
  gap: 10px;
}

.phone-code-select {
  width: 130px;
  min-width: 130px;
  max-width: 130px;
  flex-shrink: 0;
}

.phone-number-input {
  flex: 1;
  min-width: 0;
}

.phone-code-selection,
.language-selection {
  display: flex;
  align-items: center;
  gap: 6px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 100%;
  min-width: 0;
}


.flag {
  font-size: 1.1rem;
}

.flag-large {
  font-size: 1.5rem;
  margin-right: 8px;
}

.country-item {
  padding: 8px 12px;
}


/* Light Theme */
.v-theme--lightTheme {
  .form-section {
    border-color: rgba(28, 110, 227, 0.08);
  }

  .form-section-title {
    color: rgba(15, 23, 42, 0.6);
  }

  .form-label {
    color: rgba(15, 23, 42, 0.6);
  }

  .form-input {
    background: rgba(0, 0, 0, 0.03);
    border-color: rgba(28, 110, 227, 0.15);
    color: rgba(15, 23, 42, 0.95);

    &::placeholder {
      color: rgba(15, 23, 42, 0.3);
    }

    &:focus {
      border-color: #1c6ee3;
      background: rgba(28, 110, 227, 0.03);
      box-shadow: 0 0 0 3px rgba(28, 110, 227, 0.1);
    }

    &:disabled,
    &.disabled {
      background: rgba(0, 0, 0, 0.02);
      color: rgba(15, 23, 42, 0.5);
    }

    &.has-error {
      border-color: #d32f2f;
      background: rgba(211, 47, 47, 0.03);

      &:focus {
        border-color: #d32f2f;
        box-shadow: 0 0 0 3px rgba(211, 47, 47, 0.1);
      }
    }
  }

  .field-error {
    color: #d32f2f;
  }

  .form-hint {
    color: rgba(15, 23, 42, 0.4);
  }

  .btn-email-change {
    background: rgba(28, 110, 227, 0.1);
    color: #1c6ee3;
    border-color: rgba(28, 110, 227, 0.2);

    &:hover {
      background: rgba(28, 110, 227, 0.15);
    }
  }
}

/* Responsive */
@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }

  .form-group.phone-group {
    max-width: none;
  }

  .phone-input-wrapper {
    flex-direction: column;
  }

  .phone-code-select {
    width: 100%;
    max-width: none;
  }

  .email-input-wrapper {
    flex-direction: column;
  }

  .btn-email-change {
    width: 100%;
    justify-content: center;
  }
}

/* Reduced Motion */
@media (prefers-reduced-motion: reduce) {
  .form-input,
  .btn-email-change {
    transition: none;
  }
}
</style>

<!-- Unscoped styles for Vuetify components - SSR compatibility -->
<style lang="scss">
/* Prevent v-autocomplete from becoming multi-line when focused */
.form-section .v-field__input {
  flex-wrap: nowrap !important;
  overflow: hidden;
}

.form-section .v-autocomplete__selection {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

/* Phone Code Select Styling */
.form-section .phone-code-select .v-field {
  background: rgba(0, 0, 0, 0.2) !important;
  border-radius: 10px !important;
}

.form-section .phone-code-select .v-field__input {
  padding: 10px 14px;
  min-height: 44px;
}

/* Override Vuetify outline border colors */
.form-section .phone-code-select .v-field__outline__start,
.form-section .phone-code-select .v-field__outline__end,
.form-section .phone-code-select .v-field__outline__notch::before,
.form-section .phone-code-select .v-field__outline__notch::after {
  border-color: rgba(120, 163, 255, 0.15) !important;
}

/* Focus state */
.form-section .phone-code-select .v-field--focused .v-field__outline__start,
.form-section .phone-code-select .v-field--focused .v-field__outline__end,
.form-section .phone-code-select .v-field--focused .v-field__outline__notch::before,
.form-section .phone-code-select .v-field--focused .v-field__outline__notch::after {
  border-color: #00b0ff !important;
}

/* Disabled state */
.form-section .phone-code-select.disabled .v-field {
  background: rgba(255, 255, 255, 0.02) !important;
}

.form-section .phone-code-select.disabled .v-field__outline__start,
.form-section .phone-code-select.disabled .v-field__outline__end,
.form-section .phone-code-select.disabled .v-field__outline__notch::before,
.form-section .phone-code-select.disabled .v-field__outline__notch::after {
  border-color: rgba(120, 163, 255, 0.08) !important;
}

/* Light Theme */
.v-theme--lightTheme .form-section .phone-code-select .v-field {
  background: rgba(0, 0, 0, 0.03) !important;
}

.v-theme--lightTheme .form-section .phone-code-select .v-field__outline__start,
.v-theme--lightTheme .form-section .phone-code-select .v-field__outline__end,
.v-theme--lightTheme .form-section .phone-code-select .v-field__outline__notch::before,
.v-theme--lightTheme .form-section .phone-code-select .v-field__outline__notch::after {
  border-color: rgba(28, 110, 227, 0.15) !important;
}

.v-theme--lightTheme .form-section .phone-code-select .v-field--focused .v-field__outline__start,
.v-theme--lightTheme .form-section .phone-code-select .v-field--focused .v-field__outline__end,
.v-theme--lightTheme .form-section .phone-code-select .v-field--focused .v-field__outline__notch::before,
.v-theme--lightTheme .form-section .phone-code-select .v-field--focused .v-field__outline__notch::after {
  border-color: #1c6ee3 !important;
}

.v-theme--lightTheme .form-section .phone-code-select.disabled .v-field {
  background: rgba(0, 0, 0, 0.02) !important;
}
</style>
